import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import PageHero from "../components/site/PageHero";
import { CTABand } from "../components/site/ContentBlocks";
import { FadeUp } from "../components/site/Reveal";
import { BRANDS, PARENT_BRAND, BRAND_WHO } from "../data/content";

export default function OurBrands() {
    const navigate = useNavigate();

    return (
        <div className="grain bg-navy-deep" data-testid="our-brands-page">
            <Header />
            <main>
                <PageHero
                    seo={{
                        title: "Our Brands | VietClinics, Doctors Vietnam, Dental Vietnam & More — Viet Medica Ltd",
                        description:
                            "The specialist brands of Viet Medica Ltd — healthcare coordination, medical support, dental care, aesthetic treatment, visa services and travel across Vietnam.",
                        path: "/our-brands",
                    }}
                    crumbs={[{ label: "Our Brands" }]}
                    eyebrow="One International Group"
                    title="Specialist Services."
                    titleEm="One International Group."
                    copy="Each brand serves a clear specialist need while benefiting from the international network, experience and coordination of Viet Medica Ltd."
                />

                <section className="bg-ivory py-20 text-navy-deep lg:py-28" data-testid="our-brands-list">
                    <div className="mx-auto max-w-[1440px] space-y-6 px-5 lg:px-10">
                        {BRANDS.map((b, i) => (
                            <FadeUp key={b.id} delay={0.05}>
                                <article
                                    className="grid grid-cols-1 gap-8 border border-navy-deep/10 bg-white p-8 lg:grid-cols-12 lg:p-12"
                                    data-testid={`our-brand-${b.id}`}
                                >
                                    <div className="flex flex-col items-start gap-4 lg:col-span-4">
                                        {b.logo ? (
                                            <img
                                                src={b.logo}
                                                alt={`${b.name} — official logo`}
                                                loading="lazy"
                                                className="h-14 w-auto object-contain mix-blend-multiply lg:h-16"
                                            />
                                        ) : (
                                            <span className="font-serif text-3xl font-medium tracking-wide text-navy-deep">{b.name}</span>
                                        )}
                                        <span className="text-[10px] font-semibold uppercase tracking-[0.26em] text-navy-deep/45">
                                            A Viet Medica Ltd Brand
                                        </span>
                                    </div>
                                    <div className="lg:col-span-5">
                                        <h2 className="font-serif text-2xl font-normal lg:text-3xl">{b.name}</h2>
                                        <p className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-gold">
                                            Who it supports
                                        </p>
                                        <p className="mt-1.5 text-sm text-navy-deep/75">{BRAND_WHO[b.id]}</p>
                                        <p className="mt-4 text-sm leading-relaxed text-navy-deep/70">{b.full}</p>
                                        <ul className="mt-5 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                                            {b.services.map((s) => (
                                                <li key={s} className="flex items-start gap-2.5 text-[13px] text-navy-deep/70">
                                                    <Check size={13} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                                                    {s}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="flex flex-col items-start gap-3 lg:col-span-3 lg:items-end">
                                        {b.website ? (
                                            <a
                                                href={b.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`our-brand-website-${b.id}`}
                                                className="inline-flex items-center gap-2 bg-navy-deep px-6 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-gold hover:text-navy-deep"
                                            >
                                                Visit Website <ArrowUpRight size={14} />
                                            </a>
                                        ) : (
                                            <span
                                                data-testid={`our-brand-website-${b.id}`}
                                                className="inline-flex items-center border border-dashed border-navy-deep/30 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-navy-deep/45"
                                            >
                                                Website Coming Soon
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            data-testid={`our-brand-enquiry-${b.id}`}
                                            onClick={() => navigate("/contact")}
                                            className="inline-flex items-center gap-2 border border-navy-deep/25 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-navy-deep transition-colors hover:border-gold hover:text-gold"
                                        >
                                            Make an Enquiry
                                        </button>
                                    </div>
                                </article>
                            </FadeUp>
                        ))}

                        <FadeUp>
                            <article
                                className="grid grid-cols-1 items-center gap-8 border border-gold/30 bg-navy-deep p-8 lg:grid-cols-12 lg:p-12"
                                data-testid="our-brand-viet-medica"
                            >
                                <div className="lg:col-span-4">
                                    <span className="inline-flex rounded-[3px] bg-ivory px-4 py-3">
                                        <img src={PARENT_BRAND.logo} alt="Viet Medica Ltd — official logo" loading="lazy" className="h-12 w-auto" />
                                    </span>
                                </div>
                                <div className="lg:col-span-5">
                                    <h2 className="font-serif text-2xl font-normal text-ivory lg:text-3xl">Viet Medica Ltd</h2>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-300/85">{PARENT_BRAND.copy}</p>
                                </div>
                                <div className="flex lg:col-span-3 lg:justify-end">
                                    <button
                                        type="button"
                                        data-testid="our-brand-about-cta"
                                        onClick={() => navigate("/about")}
                                        className="inline-flex items-center gap-2 border border-gold/60 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-gold transition-colors hover:bg-gold hover:text-navy-deep"
                                    >
                                        About Viet Medica <ArrowUpRight size={14} />
                                    </button>
                                </div>
                            </article>
                        </FadeUp>
                    </div>
                </section>

                <section className="bg-navy-surface py-20 lg:py-28">
                    <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                        <CTABand
                            title="Not sure which brand fits your need?"
                            primary={{ label: "Contact Us", to: "/contact", testid: "brands-contact-cta" }}
                            secondary={{ label: "Global Network", to: "/global-network", testid: "brands-network-cta" }}
                        />
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
